<template>
  <div
    class="app-input"
    :class="{ horizontal, 'has-error': error }"
  >
    <div class="app-input__container">
      <!-- Label -->
      <label
        v-if="label"
        class="app-input__label"
        :for="name"
      >
        <span>{{ label }}</span>
        <!-- Error -->
        <transition name="fade">
          <span
            v-if="error"
            class="app-input__error"
          >
            {{ error }}
          </span>
        </transition>
      </label>

      <div class="app-input__field-wrapper">
        <!-- Icon -->
        <span
          v-if="$slots.icon"
          class="app-input__icon"
        >
          <slot name="icon"/>
        </span>

        <!-- Input Field -->
        <input
          :id="id || name"
          ref="input"
          class="app-input__field"
          :class="{ 'has-icon': $slots.icon }"
          :type="type"
          :value="value"
          :pattern="pattern"
          :placeholder="placeholder"
          :name="name"
          :min="min"
          :max="max"
          @input="$emit('input', $event.target.value)"
          @blur="$emit('touched', $event.target.value)"
          @keydown="preventUnwantedKeys($event)"
          :disabled="disabled"
        >
      </div>

      <!-- Hint -->
      <span
        v-if="hint"
        class="app-input__hint"
      >{{ hint }}</span>
    </div>

  </div>
</template>

<script>
export default {
  // Name
  name: 'app-input',

  // Props
  props: {
    value: [String, Number],
    placeholder: String,
    label: String,
    hint: String,
    type: {
      type: String,
      default: 'text'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    icon: String,
    error: String,
    name: String,
    horizontal: Boolean,
    id: String,
    pattern: String,
    min: Number,
    max: Number,
    isHourlyWage: Boolean
  },

  // Methods
  methods: {
    /**
     * Prevent Unwanted Key Inputs
     * @desc For Hourly Wage step, prevents all non-numberic keys expect ALLOWED_KEYS defined below.
      for all other [number] inputs, * prevents user from typing a '.', or e
     */
    preventUnwantedKeys (event) {
      const ALLOWED_KEYS = event.keyCode === 190 || event.keyCode === 110 || event.keyCode === 8 // Period (Keyboard- 190,  Numpad - 110), Backspace - 8
      if (this.type !== 'number') return true
      if (this.isHourlyWage) {
        const key = Number(event.key)
        const isNonNumericKey = isNaN(key)
        if (isNonNumericKey) {
          if (ALLOWED_KEYS) {
            return
          }
          event.preventDefault()
          return false
        }
      } else {
        switch (event.keyCode) {
          case (110): // Numpad Decimal Point
          case (190): // Period
          case (69): // 'e'
            event.preventDefault()
            return false
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
$input-border: 1px solid #d6d6d6;
$input-color-border-active: #3CBBF5;

.app-input {
  position: relative;
  margin-bottom: 15px;
  width: 100%;

  .app-input__label {
    min-height: 13px;
      @include breakpoint('tablet-portrait-up') {
        min-height: none;
      }
  }

  @include breakpoint('tablet-portrait-up') {
    margin-bottom: 20px;
  }

  @include breakpoint('tablet-landscape-up') {
    margin-left: 10px;
    margin-bottom: 15px;

    &:first-child {
      margin-left: 0;
    }
  }

  &__label {
    display: block;
    font-size: 9px;
    color: #868686;
    margin: 0 0 6px 0;
    line-height: 1.5;

    @include breakpoint('tablet-portrait-up') {
      font-size: 12px;
    }

    @include breakpoint('tablet-landscape-up') {
      font-size: 12px;
    }

    @include breakpoint('desktop-up') {
      font-size: 13px;
    }
  }

  &__container {
    position: relative;
  }

  &__hint {
    position: absolute;
    left: 0;
    bottom: -30px;
    display: block;
    font-size: 11px;
    color: #b7b7b7;
    margin: 5px 0 5px 0;

    @include breakpoint('tablet-portrait-up') {
      font-size: 14px;
    }

    @include breakpoint('desktop-up') {
      font-size: 14px;
    }
  }

  &__field-wrapper {
    position: relative;
  }

  &__field {
    display: block;
    width: 100%;
    height: 50px;
    color: #343434;
    border: $input-border;
    border-radius: 0;
    padding: 5px 15px;
    font-size: 14px;
    font-family: inherit;
    letter-spacing: 0.04em;
    -moz-appearance: textfield;
    -webkit-appearance: none;
    background-color: #fff;
    box-shadow: none;
    transition: outline 250ms ease-out,
    background-color 250ms ease-out,
    box-shadow 250ms ease-out,
    border-color 250ms ease-out;

    @include breakpoint('tablet-portrait-up') {
      height: 65px;
      font-size: 20px;
      letter-spacing: 0;
    }

    @include breakpoint('desktop-up') {
      height: 75px;
      font-size: 25px;
    }

    &:focus {
      outline: none;
      background-color: #fafafa;
      border-color: $input-color-border-active;
    }

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &.has-icon {
      padding-left: 35px;
    }
  }

  &__icon {
    position: absolute;
    left: 15px;
    font-size: 20px;
    top: 14px;

    @include breakpoint('tablet-portrait-up') {
      top: 22px;
    }

    @include breakpoint('tablet-landscape-up') {
      top: 20px;
    }

    @include breakpoint('desktop-up') {
      top: 24px;
      font-size: 25px;
    }
  }

  &__error {
    position: absolute;
    right: 2px;
    top: 75px;
    color: $theme-error;
    font-size: 12px;
    font-style: italic;
    display: inline-block;

    @include breakpoint('tablet-portrait-up') {
      position: relative;
      top: 0;
      right: 0;
      margin-left: 5px;
      font-size: 14px;
    }
  }

  ::placeholder {
    color: #b7b7b7;
    font-weight: 400;
  }

  &.has-error .app-input__field {
    border-color:  $theme-error;
    box-shadow: 0 4px 0px 0px $theme-error;
  }
}

.horizontal.app-input {
  margin-bottom: 25px;

  @include breakpoint('tablet-portrait-up') {
    margin-bottom: 30px;
  }

  &:last-child {
    margin-bottom: 0;

    @include breakpoint('tablet-landscape-up') {
      margin-bottom: 30px;
    }
  }

  &.has-error .app-input__container {
    border-color:  $theme-error;
    border: 1px solid $theme-error;
    box-shadow: 0 4px 0px 0px $theme-error;
  }

  &.has-error .app-input__field {
    border: none;
    box-shadow: none;
  }
}

.horizontal .app-input {
  &__container {
    display: flex;
    border: $input-border;
    transition: outline 250ms ease-out,
    background-color 250ms ease-out,
    box-shadow 250ms ease-out,
    border-color 250ms ease-out;
  }

  &__label {
    order: 2;
    width: 50%;
    border: none;
    border-left: $input-border;
    margin-bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    color: #363636;

    @include breakpoint('tablet-portrait-up') {
      font-size: 30px;
    }

    @include breakpoint('tablet-landscape-up') {
      font-size: 20px;
    }

    @include breakpoint('desktop-up') {
      font-size: 25px;
    }
  }

  &__field-wrapper {
    display: flex;
    order: 1;
    width: 50%;
  }

  &__field {
    text-align: center;
    border: none;
  }

  &__error {
    position: absolute;
    top: -15px;
    right: 0px;

    @include breakpoint('tablet-portrait-up') {
      top: -17px;
    }

    @include breakpoint('tablet-landscape-up') {
      top: -20px;
    }

    @include breakpoint('desktop-up') {
      top: -25px;
    }
  }
}
</style>
